import * as React from "react"
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog"
import { UnitfieldIcon } from '@unitfield/ui/icons';

import { cn } from "../../lib/utils"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./alert-dialog"
import { Button, buttonVariants } from "./button"
import { LoadingIndicator } from "./pixel-grid"
import "./quick-create-dialog.css"
import "./confirm-dialog.css"

export type ConfirmDialogTone = "destructive" | "warning" | "default"

export interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: React.ReactNode
  description?: React.ReactNode
  tone?: ConfirmDialogTone
  confirmLabel?: string
  cancelLabel?: string
  icon?: string
  onConfirm: () => void | Promise<void>
  loading?: boolean
  /** Optional override for the confirm button class (rare). */
  confirmClassName?: string
  /** Stops auto-close after onConfirm resolves (caller controls open). */
  manualClose?: boolean
  /**
   * `panel` is the task-panel confirmation (e.g. "Discard changes?"): the
   * quick-create dialog geometry, no icon, cancel first and focused.
   */
  appearance?: "alert" | "panel"
  /**
   * Where focus lands when a `panel` confirm opens. `cancel` (default) is the safe button; `dialog` puts it on the dialog container with
   * no ring (the ring shows only on keyboard focus, `:focus-visible`), for confirms opened with the pointer.
   */
  initialFocus?: "cancel" | "dialog"
  /** Error from a failed confirm, shown above the buttons (panel appearance). */
  error?: React.ReactNode
  children?: React.ReactNode
}

const toneConfig: Record<ConfirmDialogTone, { icon: string; iconClass: string }> = {
  destructive: {
    icon: "warning",
    iconClass: "text-status-danger-1 bg-status-danger-0 ring-status-danger-1/20",
  },
  warning: {
    icon: "warning-circle",
    iconClass: "text-status-warning-1 bg-status-warning-0 ring-status-warning-1/30",
  },
  default: {
    icon: "info-circle",
    iconClass: "text-foreground bg-muted ring-border/50",
  },
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  tone = "default",
  confirmLabel,
  cancelLabel = "Cancel",
  icon,
  onConfirm,
  loading: controlledLoading,
  confirmClassName,
  manualClose,
  appearance = "alert",
  initialFocus = "cancel",
  error,
  children,
}: ConfirmDialogProps) {
  const [internalLoading, setInternalLoading] = React.useState(false)
  const loading = controlledLoading ?? internalLoading
  const config = toneConfig[tone]
  const resolvedIcon = icon ?? config.icon
  const resolvedConfirmLabel =
    confirmLabel ?? (tone === "destructive" ? "Delete" : tone === "warning" ? "Continue" : "Confirm")

  async function handleConfirm(event: React.MouseEvent) {
    event.preventDefault()
    if (loading) return
    setInternalLoading(true)
    try {
      await onConfirm()
      if (!manualClose) onOpenChange(false)
    } finally {
      setInternalLoading(false)
    }
  }

  if (appearance === "panel") {
    return (
      <AlertDialogPrimitive.Root open={open} onOpenChange={next => { if (!loading || next) onOpenChange(next) }}>
        <AlertDialogPrimitive.Portal>
          <AlertDialogPrimitive.Overlay className="quick-create-overlay" />
          <AlertDialogPrimitive.Content data-slot="confirm-dialog" data-tone={tone}
            className="quick-create-dialog confirm-dialog-panel"
            {...(!description && { "aria-describedby": undefined })}
            onOpenAutoFocus={initialFocus === "dialog" ? event => { event.preventDefault(); (event.currentTarget as HTMLElement).focus({ preventScroll: true }) } : undefined}
            onEscapeKeyDown={event => { if (loading) event.preventDefault() }}>
            <header className="quick-create-header">
              <AlertDialogPrimitive.Title className="quick-create-title">{title}</AlertDialogPrimitive.Title>
              {description ? <AlertDialogPrimitive.Description className="confirm-dialog-panel-description">{description}</AlertDialogPrimitive.Description> : null}
            </header>
            {children ? <div className="confirm-dialog-panel-body">{children}</div> : null}
            {error ? <p role="alert" className="panel-form-notice confirm-dialog-panel-error">{error}</p> : null}
            <footer className="quick-create-footer">
              <AlertDialogPrimitive.Cancel asChild>
                <Button type="button" variant="secondary" className="quick-create-button" disabled={loading}>{cancelLabel}</Button>
              </AlertDialogPrimitive.Cancel>
              <Button type="button" variant={tone === "destructive" ? "destructive" : "default"} className={cn("quick-create-button", confirmClassName)}
                onClick={handleConfirm} disabled={loading} pending={loading} pendingLabel={resolvedConfirmLabel} pendingAriaLabel={resolvedConfirmLabel}>
                {resolvedConfirmLabel}
              </Button>
            </footer>
          </AlertDialogPrimitive.Content>
        </AlertDialogPrimitive.Portal>
      </AlertDialogPrimitive.Root>
    )
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-start gap-3">
            <div className={cn("flex size-10 shrink-0 items-center justify-center rounded-full ring-1 ring-inset", config.iconClass)}>
              <UnitfieldIcon name={resolvedIcon} className="size-5" aria-hidden />
            </div>
            <div className="space-y-1.5">
              <AlertDialogTitle>{title}</AlertDialogTitle>
              {description ? <AlertDialogDescription>{description}</AlertDialogDescription> : null}
            </div>
          </div>
        </AlertDialogHeader>

        {children ? <div className="text-sm text-muted-foreground">{children}</div> : null}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={loading}
            aria-busy={loading}
            // Destructive keeps a red label on the tonal fill (house rule), never a red block.
            className={cn(buttonVariants({ variant: tone === "destructive" ? "secondary" : "default" }), tone === "destructive" && "font-semibold text-destructive hover:text-destructive", confirmClassName)}
          >
            {loading ? (
              <LoadingIndicator preset="compact" decorative />
            ) : null}
            {resolvedConfirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
